import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="text-lg md:text-xl font-bold font-heading text-foreground-950 whitespace-nowrap">
              Superbuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/article/ultimate-superbuy-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Guides
            </Link>

            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Safety
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 px-5 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Superbuy Sheet
            </a>
          </div>

          <div className="hidden md:block">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 px-5 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-0 flex flex-col max-h-[80vh] overflow-y-auto">
            <div className="py-4 px-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3 px-2">Shop by Category</p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 shrink-0">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    <span className="text-xs leading-tight line-clamp-1">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 py-3 px-4 flex flex-col gap-1">
              <Link
                to="/"
                className="flex items-center gap-3 py-2.5 px-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-home-4-line text-base"></i>
                </span>
                Home
              </Link>
              <Link
                to="/article/ultimate-superbuy-spreadsheet-guide"
                className="flex items-center gap-3 py-2.5 px-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-book-open-line text-base"></i>
                </span>
                Ultimate Guide
              </Link>
              <Link
                to="/"
                className="flex items-center gap-3 py-2.5 px-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-shield-check-line text-base"></i>
                </span>
                Safety
              </Link>
            </div>

            <div className="border-t border-background-200/60 py-4 px-4 flex flex-col gap-2.5">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 px-5 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-external-link-line text-base"></i>
                Superbuy Sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 px-5 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-shopping-cart-2-line text-base"></i>
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}